export interface Product {
  id: string;
  label: string;
  description: string;
  /** Future production sub-domain SPA (atlas.fololabs.io, …). */
  url: string;
  /**
   * Live demo unit, if one is deployed. The product sub-domains don't
   * exist yet — for the demo, the products that have a shipped UI run as
   * public routes inside the app.fololabs.io bundle (on the dev API).
   * `undefined` → no unit deployed yet → in-app DEMO placeholder.
   */
  unitUrl?: string;
  /** Cognito group prefix that grants access (e.g. "atlas" matches "atlas_admin", "atlas_viewer"). */
  groupPrefix: string;
  color: string;
}

export const PRODUCTS: Product[] = [
  {
    id: 'atlas',
    label: 'Sector Atlas',
    description: 'Station profiles, benchmarking, and regional water maps.',
    url: 'https://atlas.fololabs.io',
    unitUrl: 'https://atlas.fololabs.io',
    groupPrefix: 'atlas',
    color: 'aquifer',
  },
  {
    id: 'preflight',
    label: 'Pre-flight',
    description: 'Compliance checklist generation and audit tracking.',
    url: 'https://preflight.fololabs.io',
    unitUrl: 'https://preflight.fololabs.io/compliance',
    groupPrefix: 'preflight',
    color: 'spring',
  },
  {
    id: 'processlab',
    label: 'Process Lab',
    description: 'Treatment process analytics and performance modelling.',
    url: 'https://processlab.fololabs.io',
    unitUrl: 'https://processlab.fololabs.io',
    groupPrefix: 'processlab',
    color: 'aquifer',
  },
  {
    id: 'sourcing',
    label: 'Sourcing',
    description: 'Equipment and chemical procurement workflows.',
    url: 'https://sourcing.fololabs.io',
    unitUrl: 'https://sourcing.fololabs.io',
    groupPrefix: 'sourcing',
    color: 'spring',
  },
];

/** Return products the user can access based on their Cognito groups. */
export function accessibleProducts(groups: string[]): Product[] {
  return PRODUCTS.filter((p) =>
    groups.some((g) => g === p.groupPrefix || g.startsWith(`${p.groupPrefix}_`)),
  );
}

export function productById(id: string): Product | undefined {
  return PRODUCTS.find((p) => p.id === id);
}

/** True when a tile leaves the launcher to an external unit (vs in-app). */
export function opensExternally(product: Product, demo: boolean): boolean {
  return demo ? product.unitUrl != null : true;
}

/**
 * Where a tile navigates — always the live unit when one exists.
 * - Has a unit → the unit URL (with `?demo=1` in demo so the unit can show
 *   a DEMO banner). This is the live product: app.fololabs.io routes for
 *   Atlas/Pre-flight, *.pages.dev for Process Lab/Sourcing.
 * - No unit + demo → in-app `#/p/{id}` placeholder.
 * - No unit + prod → the future product sub-domain (placeholder until DNS).
 */
export function productHref(product: Product, demo: boolean): string {
  if (product.unitUrl) {
    return demo ? appendDemoFlag(product.unitUrl) : product.unitUrl;
  }
  return demo ? `#/p/${product.id}` : product.url;
}

function appendDemoFlag(url: string): string {
  return url.includes('?') ? `${url}&demo=1` : `${url}?demo=1`;
}
